import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmtN = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '-';
  if (typeof d === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
  if (typeof d === 'string' && d.startsWith('/Date(')) {
    const ms = parseInt(d.replace(/\/Date\((\d+)[^)]*\)\//, '$1'));
    if (!isNaN(ms)) {
      const dt = new Date(ms);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
    }
  }
  const p = new Date(d);
  if (isNaN(p)) return '-';
  return `${String(p.getDate()).padStart(2,'0')}/${String(p.getMonth()+1).padStart(2,'0')}/${p.getFullYear()}`;
};
const fmt2Date = (date) =>
  `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;

const PRIMARY     = '#1c2266';
const PRIMARY_MID = '#3f51b5';
const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const ageBucket = (days) => {
  if (days <= 30) return { bg: '#dcfce7', color: '#166534' };
  if (days <= 60) return { bg: '#fef9c3', color: '#854d0e' };
  if (days <= 90) return { bg: '#fed7aa', color: '#9a3412' };
  return              { bg: '#fee2e2', color: '#991b1b' };
};

const ClientGroupOutstandingReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [groups, setGroups]     = useState([]);
  const [groupId, setGroupId]   = useState('');
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);
  const [search, setSearch]     = useState('');

  // Load ledger groups on mount
  useEffect(() => {
    if (!companyId || !token) return;
    fetchData('ClientAndVendorApi', `/LedgerGroupList/${companyId}`, token)
      .then(res => setGroups(res?.response_data || res || []))
      .catch(() => setGroups([]));
  }, [companyId, token]);

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'SaleReportApi',
        `/ClientGroupOutstanding/${companyId}/${branchId}/${groupId || 0}/${fromDate}/${toDate}`,
        token
      );
      const rows = res?.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch client group outstanding report');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let rows = data;
    if (groupId) rows = rows.filter(r => String(r.ClientGroupId) === String(groupId));
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(r =>
        (r.CustomerName    || '').toLowerCase().includes(q) ||
        (r.BillNo          || '').toLowerCase().includes(q) ||
        (r.SaleManName     || '').toLowerCase().includes(q) ||
        (r.ClientGroupName || '').toLowerCase().includes(q) ||
        (r.ContactNumber   || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, groupId, search]);

  const grandTotals = useMemo(() => filtered.reduce((acc, r) => ({
    billAmount:    acc.billAmount    + (parseFloat(r.BillAmount)    || 0),
    paidAmount:    acc.paidAmount    + (parseFloat(r.PaidAmount)    || 0),
    balanceAmount: acc.balanceAmount + (parseFloat(r.BalanceAmount) || 0),
  }), { billAmount: 0, paidAmount: 0, balanceAmount: 0 }), [filtered]);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'ClientGroupOutstanding', 0);
    if (!filtered.length) return;
    const headers = ['#', 'Bill No', 'Bill Date', 'Due Date', 'Salesman',
      'Client Group', 'Customer', 'Contact',
      'Bill Amount', 'Paid Amount', 'Balance Amount', 'Bill Age Days', 'Overdue Days'];
    const rows = filtered.map((r, i) => [
      i + 1, r.BillNo, fmtDate(r.BillDate), fmtDate(r.PaymentDueDate),
      r.SaleManName, r.ClientGroupName, r.CustomerName, r.ContactNumber,
      r.BillAmount, r.PaidAmount, r.BalanceAmount, r.BillAgeDays, r.OverDueDays,
    ]);

      await exportStyledExcel({
          reportTitle: 'CLIENT GROUP OUTSTANDING (RECEIVABLE)',
          sheetName: 'Client Group Outstanding',
          fileName: `ClientGroupOutstanding_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: headers,
          rows,
          totalsRow: ['', '', '', '', '', '', '', 'Totals',
        fmtN(grandTotals.billAmount), fmtN(grandTotals.paidAmount), fmtN(grandTotals.balanceAmount), '', ''],
          columnWidths: [4, 10, 12, 12, 16, 20, 22, 14, 14, 14, 14, 12, 12],
      });
  };

  const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #9fa8da', color: '#fff', background: PRIMARY_MID };
  const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading client group outstanding report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #2d3494 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Client Group Outstanding
            <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Outstanding Receivable</span>
          </span>

          <div style={{ flex: 1, minWidth: 4 }} />

          <input
            placeholder="Search customer / bill / group..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 180, outline: 'none' }}
          />

          <SearchableDropdown
            value={groupId}
            onChange={v => setGroupId(v)}
            options={[{ value: '', label: '— All Groups —' }, ...groups.map(g => ({ value: String(g.Id), label: g.Name }))]}
            placeholder="— All Groups —"
            minWidth={160}
            primaryColor={PRIMARY}
          />

          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />

          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Meta bar */}
        {fetched && (
          <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span>{fromDate} to {toDate}</span>
            <span>|</span>
            <span>{filtered.length} bills{(search || groupId) ? ` (filtered from ${data.length})` : ''}</span>
            <span>|</span>
            <span>Bill Amt: <strong style={{ color: PRIMARY }}>{fmtN(grandTotals.billAmount)}</strong></span>
            <span>|</span>
            <span>Paid: <strong style={{ color: '#059669' }}>{fmtN(grandTotals.paidAmount)}</strong></span>
            <span>|</span>
            <span>Balance: <strong style={{ color: '#b91c1c' }}>{fmtN(grandTotals.balanceAmount)}</strong></span>
          </div>
        )}

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 140px)' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 400, width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                <th style={{ ...thBase, textAlign: 'left' }}>Bill No</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Bill Date</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Due Date</th>
                <th style={{ ...thBase, textAlign: 'left', minWidth: 120 }}>Salesman</th>
                <th style={{ ...thBase, textAlign: 'left', minWidth: 140 }}>Client Group</th>
                <th style={{ ...thBase, textAlign: 'left', minWidth: 160 }}>Customer</th>
                <th style={{ ...thBase, textAlign: 'left' }}>Contact</th>
                <th style={{ ...thBase, textAlign: 'right' }}>Bill Amount</th>
                <th style={{ ...thBase, textAlign: 'right' }}>Paid Amount</th>
                <th style={{ ...thBase, textAlign: 'right', background: '#0f4c81' }}>Balance</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Bill Age</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Overdue Days</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={13} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click Refresh'}
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => {
                  const isOverdue = (r.OverDueDays || 0) > 0;
                  const bucket    = ageBucket(r.BillAgeDays || 0);
                  const rowBg     = isOverdue
                    ? (i % 2 === 0 ? '#fff8f8' : '#fff0f0')
                    : (i % 2 === 0 ? '#fff' : '#e8eaf6');
                  return (
                    <tr key={`${r.SaleId}-${i}`} style={{ background: rowBg }}>
                      <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{r.RN || i + 1}</td>
                      <td style={{ ...tdBase, fontWeight: 500, color: PRIMARY }}>{r.BillNo || '-'}</td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>{fmtDate(r.BillDate)}</td>
                      <td style={{ ...tdBase, textAlign: 'center', color: isOverdue ? '#dc2626' : '#374151', fontWeight: isOverdue ? 600 : 400 }}>
                        {fmtDate(r.PaymentDueDate)}
                      </td>
                      <td style={{ ...tdBase, color: '#374151' }}>{r.SaleManName || '-'}</td>
                      <td style={{ ...tdBase, fontWeight: 500, color: '#374151' }}>{r.ClientGroupName || '-'}</td>
                      <td style={{ ...tdBase, fontWeight: 500 }}>{r.CustomerName || '-'}</td>
                      <td style={{ ...tdBase, color: '#0277bd' }}>{r.ContactNumber || '-'}</td>
                      <td style={{ ...tdBase, textAlign: 'right', fontWeight: 600 }}>{fmtN(r.BillAmount)}</td>
                      <td style={{ ...tdBase, textAlign: 'right', color: '#059669' }}>{fmtN(r.PaidAmount)}</td>
                      <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#b91c1c', background: '#fff5f5' }}>{fmtN(r.BalanceAmount)}</td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>
                        <span style={{ padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: bucket.bg, color: bucket.color }}>
                          {r.BillAgeDays ?? '-'}
                        </span>
                      </td>
                      <td style={{ ...tdBase, textAlign: 'center', color: isOverdue ? '#dc2626' : '#374151', fontWeight: isOverdue ? 700 : 400 }}>
                        {r.OverDueDays ?? '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: '#e8eaf6', fontWeight: 700 }}>
                  <td colSpan={8} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtN(grandTotals.billAmount)}</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtN(grandTotals.paidAmount)}</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#b91c1c', background: '#fecaca' }}>{fmtN(grandTotals.balanceAmount)}</td>
                  <td colSpan={2} style={{ ...tdBase }} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClientGroupOutstandingReport;
